import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import {
  LucideShare2,
  LucideX,
  LucidePencil,
  LucideEye,
  LucideUser,
  LucideSearch,
  LucideCheck,
  LucideLoader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/modals/Modal";
import { BACKEND_URL } from "@/constants/api";

/**
 * Modal for sharing a class with other users and managing existing members.
 * Three-column layout: teacher browser | pending invite | current members.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.reportGroupId
 * @param {object} props.classData - Class object with name and sharedWith array
 * @param {(updater: Function) => void} props.onClassDataChange - State updater for classData
 * @param {() => Promise<void>} props.onRefresh - Callback to refetch class data after sharing
 */
export function ShareClassModal({
  open,
  onClose,
  reportGroupId,
  classData,
  onClassDataChange,
  onRefresh,
}) {
  const { getToken } = useAuth();

  // Left column state
  const [school, setSchool] = useState(null); // { schoolId, schoolName, members }
  const [colleaguesLoading, setColleaguesLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Middle column state
  const [selected, setSelected] = useState(new Map()); // userId -> { name, email }
  const [pendingPermission, setPendingPermission] = useState("view");
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");

  // Fallback email input (shown when teacher has no schools)
  const [shareEmail, setShareEmail] = useState("");

  // Right column state
  const [confirmRemove, setConfirmRemove] = useState(null);

  // Pending share requests (awaiting admin approval)
  const [pendingShares, setPendingShares] = useState([]); // [{ _id, name, email }]

  useEffect(() => {
    if (!open) return;
    setSelected(new Map());
    setSearch("");
    setShareError("");
    setShareEmail("");
    setPendingPermission("view");
    setPendingShares([]);

    setColleaguesLoading(true);
    getToken()
      .then((token) =>
        Promise.all([
          fetch(`${BACKEND_URL}api/schools/colleagues`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then((res) => (res.ok ? res.json() : [])),
          fetch(`${BACKEND_URL}api/shared-classes/${reportGroupId}/pending-requests`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then((res) => (res.ok ? res.json() : [])),
        ])
      )
      .then(([colleagues, pendingRequests]) => {
        setSchool(colleagues[0] ?? null);
        setPendingShares(
          pendingRequests.map((r) => ({
            _id: r._id,
            name: r.targetUserId?.name ?? "",
            email: r.targetUserId?.email ?? "",
          }))
        );
      })
      .catch((err) => console.error("Error fetching modal data:", err))
      .finally(() => setColleaguesLoading(false));
  }, [open, getToken, reportGroupId]);

  function handleClose() {
    if (!confirmRemove) {
      onClose();
      setShareError("");
    }
  }

  // Set of userIds already shared with this class
  const alreadySharedIds = new Set(
    (classData?.sharedWith || []).map((m) => String(m.userId?._id ?? m.userId))
  );

  function toggleTeacher(userId, name, email) {
    if (alreadySharedIds.has(userId)) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.set(userId, { name, email });
      }
      return next;
    });
  }

  const filteredMembers = (school?.members ?? []).filter((m) => {
    const q = search.toLowerCase();
    return !q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
  });

  // Send invites to all selected teachers
  async function handleShare() {
    if (selected.size === 0) return;
    setSharing(true);
    setShareError("");

    let successCount = 0;
    const newPending = [];
    let errorMsg = "";

    for (const [, { name, email }] of selected.entries()) {
      try {
        const token = await getToken();
        const res = await fetch(
          `${BACKEND_URL}api/shared-classes/${reportGroupId}/share`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, permission: pendingPermission }),
          }
        );
        const body = await res.json();
        if (!res.ok) {
          errorMsg = body.message || "Failed to share with some teachers";
        } else if (body.pending) {
          newPending.push({ _id: body.requestId, name, email });
        } else {
          successCount++;
        }
      } catch {
        errorMsg = "Failed to share with some teachers";
      }
    }

    setSharing(false);
    if (errorMsg) setShareError(errorMsg);

    if (successCount > 0) {
      toast.success(
        `Class shared with ${successCount} teacher${successCount > 1 ? "s" : ""}.`
      );
      setSelected(new Map());
      await onRefresh();
    }
    if (newPending.length > 0) {
      toast.success(
        `${newPending.length} share request${newPending.length > 1 ? "s" : ""} submitted - awaiting admin approval.`
      );
      setPendingShares((prev) => {
        const existingIds = new Set(prev.map((p) => String(p._id)));
        return [...prev, ...newPending.filter((p) => !existingIds.has(String(p._id)))];
      });
      setSelected(new Map());
    }
  }

  // Fallback: email-based share for teachers with no school
  async function handleEmailShare(e) {
    e.preventDefault();
    if (!shareEmail) return;
    setSharing(true);
    setShareError("");
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/shared-classes/${reportGroupId}/share`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: shareEmail, permission: pendingPermission }),
        }
      );
      const body = await res.json();
      if (!res.ok) {
        setShareError(body.message || "Failed to share class");
        return;
      }
      setShareEmail("");
      if (body.pending) {
        toast.success("Share request submitted - awaiting admin approval.");
        setPendingShares((prev) => {
          const id = String(body.requestId);
          if (prev.some((p) => String(p._id) === id)) return prev;
          return [...prev, { _id: body.requestId, name: "", email: shareEmail }];
        });
      } else {
        toast.success(`Class shared with ${shareEmail}.`);
        await onRefresh();
      }
    } catch (err) {
      console.error("Error sharing class:", err);
      setShareError("Failed to share class");
    } finally {
      setSharing(false);
    }
  }

  async function handleRemoveMember(memberId, memberLabel) {
    setConfirmRemove({ id: memberId, label: memberLabel });
  }

  async function doRemoveMember() {
    const { id: memberId, label: memberLabel } = confirmRemove;
    setConfirmRemove(null);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/shared-classes/${reportGroupId}/members/${memberId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed");
      onClassDataChange((prev) => ({
        ...prev,
        sharedWith: prev.sharedWith.filter((m) => {
          const mId = String(m.userId?._id ?? m.userId ?? m._id);
          return mId !== memberId;
        }),
      }));
      toast.success(`${memberLabel} has been removed from the class.`);
    } catch (err) {
      console.error("Error removing member:", err);
      toast.error("Failed to remove member. Please try again.");
    }
  }

  async function handleUpdatePermission(memberId, newPermission) {
    const previousData = classData;
    onClassDataChange((prev) => ({
      ...prev,
      sharedWith: prev.sharedWith.map((m) => {
        const mId = String(m.userId?._id ?? m.userId ?? m._id);
        return mId === memberId ? { ...m, permission: newPermission } : m;
      }),
    }));
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/shared-classes/${reportGroupId}/members/${memberId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ permission: newPermission }),
        }
      );
      if (!res.ok) throw new Error("Failed");
      toast.success(
        `Permission updated to "${newPermission === "edit" ? "Can edit" : "View only"}".`
      );
    } catch (err) {
      console.error("Error updating permission:", err);
      onClassDataChange(previousData);
      toast.error("Failed to update permission. Please try again.");
    }
  }

  const members = classData?.sharedWith || [];
  const noSchool = !colleaguesLoading && !school;

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        maxWidth="max-w-5xl"
        maxHeight="max-h-[90vh]"
      >
        {/* Custom header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <LucideShare2 size={15} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground leading-tight">
                Share &quot;{classData?.name}&quot;
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Invite colleagues to collaborate on this class.
              </p>
            </div>
          </div>
          <button
            onClick={() => { handleClose(); setConfirmRemove(null); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <LucideX size={15} />
          </button>
        </div>

        <div className="h-px bg-border shrink-0" />

        {/* Three-column body */}
        <div className="flex divide-x divide-border overflow-hidden" style={{ height: "500px" }}>

          {/* LEFT: Find Teachers */}
          <div className="w-[260px] flex flex-col shrink-0">
            <div className="px-4 pt-4 pb-3 shrink-0">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-3">
                Find Teachers
              </p>
              <div className="relative">
                <LucideSearch
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="h-9 w-full rounded-lg border border-input bg-background pl-8 pr-3 text-xs focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 pb-4">
              {colleaguesLoading ? (
                <div className="flex items-center justify-center py-10">
                  <LucideLoader2 size={16} className="animate-spin text-muted-foreground" />
                </div>
              ) : noSchool ? (
                <div className="flex flex-col items-center justify-center py-10 text-center px-3">
                  <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center mb-2">
                    <LucideUser size={16} className="text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground font-medium">No school found</p>
                  <p className="text-[11px] text-muted-foreground/60 mt-1 leading-relaxed">
                    Join a school to browse colleagues
                  </p>
                </div>
              ) : filteredMembers.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">
                  No teachers match your search
                </p>
              ) : (
                <div className="space-y-0.5">
                  {filteredMembers.map((member) => {
                    const uid = String(member.userId);
                    return (
                      <TeacherRow
                        key={uid}
                        name={member.name}
                        email={member.email}
                        isSelected={selected.has(uid)}
                        isAdded={alreadySharedIds.has(uid)}
                        onClick={() => toggleTeacher(uid, member.name, member.email)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* MIDDLE: Pending Invite */}
          <div className="w-[280px] flex flex-col shrink-0">
            <div className="px-5 pt-4 pb-3 shrink-0">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                Invite
              </p>
            </div>

            {noSchool ? (
              // Fallback email input when teacher has no schools
              <form onSubmit={handleEmailShare} className="flex-1 px-5 pb-5 flex flex-col gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">Email address</label>
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    placeholder="colleague@school.ca"
                    required
                    className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                </div>
                <PermissionSection permission={pendingPermission} onChange={setPendingPermission} />
                {shareError && <p className="text-xs text-destructive">{shareError}</p>}
                <Button
                  type="submit"
                  disabled={sharing || !shareEmail}
                  className="w-full h-10 text-sm mt-auto"
                >
                  {sharing ? "Sending..." : "Send invite"}
                </Button>
              </form>
            ) : (
              <div className="flex-1 flex flex-col px-5 pb-5 min-h-0">
                {/* Selected teacher chips */}
                <div className="flex-1 overflow-y-auto min-h-0 mb-4">
                  {selected.size === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-6">
                      <p className="text-xs text-muted-foreground">Select colleagues on the left</p>
                      <p className="text-[11px] text-muted-foreground/60 mt-1">
                        They&apos;ll appear here
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 pt-1">
                      {Array.from(selected.entries()).map(([uid, { name, email }]) => (
                        <SelectedTeacherChip
                          key={uid}
                          name={name}
                          email={email}
                          onRemove={() => toggleTeacher(uid, name, email)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Permission + send button */}
                <div className="space-y-3 shrink-0">
                  <PermissionSection permission={pendingPermission} onChange={setPendingPermission} />
                  {shareError && <p className="text-xs text-destructive">{shareError}</p>}
                  <Button
                    type="button"
                    onClick={handleShare}
                    disabled={sharing || selected.size === 0}
                    className="w-full h-10 text-sm"
                  >
                    {sharing
                      ? "Sending..."
                      : selected.size > 1
                      ? `Send ${selected.size} invites`
                      : "Send invite"}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Current Members */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-5 pt-4 pb-3 shrink-0">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                Members{members.length > 0 ? ` (${members.length})` : ""}
              </p>
            </div>

            {members.length === 0 && pendingShares.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-5">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mb-2">
                  <LucideUser size={18} className="text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">No members yet</p>
                <p className="text-[11px] text-muted-foreground/60 mt-0.5">
                  Invite someone on the left
                </p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-2.5">
                {members.map((member) => (
                  <MemberRow
                    key={String(member.userId?._id ?? member.userId ?? member._id)}
                    member={member}
                    onRemove={handleRemoveMember}
                    onUpdatePermission={handleUpdatePermission}
                  />
                ))}
                {pendingShares.length > 0 && (
                  <>
                    {members.length > 0 && <div className="h-px bg-border" />}
                    {pendingShares.map((p) => (
                      <PendingMemberRow key={String(p._id)} name={p.name} email={p.email} />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {confirmRemove && (
        <ConfirmRemoveModal
          confirmRemove={confirmRemove}
          onCancel={() => setConfirmRemove(null)}
          onConfirm={doRemoveMember}
        />
      )}
    </>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function TeacherRow({ name, email, isSelected, isAdded, onClick }) {
  return (
    <button
      type="button"
      disabled={isAdded}
      onClick={onClick}
      className={[
        "w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left transition-colors",
        isAdded
          ? "opacity-50 cursor-default"
          : isSelected
          ? "bg-primary/10 hover:bg-primary/15"
          : "hover:bg-muted",
      ].join(" ")}
    >
      <div
        className={[
          "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
          isSelected
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground",
        ].join(" ")}
      >
        {isSelected ? (
          <LucideCheck size={11} />
        ) : (
          (name || email || "?").slice(0, 2).toUpperCase()
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{name || email}</p>
        {name && <p className="text-[10px] text-muted-foreground truncate">{email}</p>}
      </div>
      {isAdded && (
        <span className="text-[10px] text-muted-foreground shrink-0">Added</span>
      )}
    </button>
  );
}

function SelectedTeacherChip({ name, email, onRemove }) {
  const initials = (name || email || "?").slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-muted/50 border border-border">
      <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold uppercase shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{name || email}</p>
        {name && <p className="text-[10px] text-muted-foreground truncate">{email}</p>}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="w-5 h-5 rounded flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
      >
        <LucideX size={12} />
      </button>
    </div>
  );
}

function PermissionSection({ permission, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-foreground">Permission</label>
      <div className="grid grid-cols-2 gap-2">
        <PermissionCard
          icon={<LucideEye size={16} />}
          label="View only"
          description="Can read reports"
          active={permission === "view"}
          onClick={() => onChange("view")}
        />
        <PermissionCard
          icon={<LucidePencil size={16} />}
          label="Can edit"
          description="Full write access"
          active={permission === "edit"}
          onClick={() => onChange("edit")}
        />
      </div>
    </div>
  );
}

function PermissionCard({ icon, label, description, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-all",
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground",
      ].join(" ")}
    >
      {icon}
      <span>{label}</span>
      <span className="text-[10px] font-normal opacity-60 leading-tight text-center">
        {description}
      </span>
    </button>
  );
}

function MemberRow({ member, onRemove, onUpdatePermission }) {
  const memberId = String(member.userId?._id ?? member.userId ?? member._id);
  const memberLabel = member.userId?.name ?? member.userId?.email ?? memberId;
  const memberEmail = member.userId?.email;
  const initials = memberLabel.slice(0, 2).toUpperCase();

  return (
    <div className="p-3 rounded-xl border border-border group hover:bg-muted/30 transition-colors">
      <div className="flex items-center gap-3">
        <div
          className={[
            "w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold uppercase shrink-0",
            member.permission === "edit"
              ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
              : "bg-primary/10 text-primary",
          ].join(" ")}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">{memberLabel}</p>
          {memberEmail && memberEmail !== memberLabel && (
            <p className="text-[10px] text-muted-foreground truncate">{memberEmail}</p>
          )}
        </div>
        <button
          onClick={() => onRemove(memberId, memberLabel)}
          className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all shrink-0"
        >
          <LucideX size={13} />
        </button>
      </div>

      <div className="mt-2.5 ml-11">
        <div className="inline-flex items-center rounded-lg border border-border overflow-hidden">
          <PermissionToggleButton
            icon={<LucideEye size={11} />}
            label="View only"
            active={member.permission === "view"}
            onClick={() => onUpdatePermission(memberId, "view")}
          />
          <div className="w-px self-stretch bg-border shrink-0" />
          <PermissionToggleButton
            icon={<LucidePencil size={11} />}
            label="Can edit"
            active={member.permission === "edit"}
            onClick={() => onUpdatePermission(memberId, "edit")}
          />
        </div>
      </div>
    </div>
  );
}

function PendingMemberRow({ name, email }) {
  const label = name || email || "Unknown";
  const initials = label.slice(0, 2).toUpperCase();

  return (
    <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/50 dark:border-amber-800/50 dark:bg-amber-950/20">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 flex items-center justify-center text-[11px] font-bold uppercase shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">{label}</p>
          {name && email && name !== email && (
            <p className="text-[10px] text-muted-foreground truncate">{email}</p>
          )}
        </div>
        <span className="shrink-0 inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400 ring-1 ring-inset ring-amber-300/60 dark:ring-amber-700/50">
          Awaiting approval
        </span>
      </div>
    </div>
  );
}

function PermissionToggleButton({ icon, label, active, onClick }) {
  return (
    <button
      type="button"
      disabled={active}
      onClick={onClick}
      className={[
        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors",
        active
          ? "bg-primary text-primary-foreground cursor-default"
          : "text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer",
      ].join(" ")}
    >
      {icon}
      {label}
    </button>
  );
}

function ConfirmRemoveModal({ confirmRemove, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 60 }}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0">
            <LucideX size={16} className="text-destructive" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Remove member?</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              <span className="font-medium text-foreground">{confirmRemove.label}</span>{" "}
              will immediately lose access to this class and all its reports.
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="h-9 px-4 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="h-9 px-4 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}
